import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'
import type { RunbookTask } from '@/types/entities'
import { createRunbookColumns } from '../columns-runbook'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Plus, Save, RotateCcw, ChevronDown, Pencil, Trash2, Code2, Image as ImageIcon, Timer } from 'lucide-react'
import { TranslatableText } from '@/components/ui/TranslatableText'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { validateDependencies } from '../utils/validateDependencies'
import { CardTableViewToggle } from '@/components/ui/card-table-view-toggle'
import { TaskImagePicker } from './TaskImagePicker'

interface RunbookTableProps {
    tasks: RunbookTask[]
    onSave: (tasks: RunbookTask[]) => void
    autoUpdate: boolean
    onAutoUpdateChange: (autoUpdate: boolean) => void
    isSaving?: boolean
    hideToolbar?: boolean // Hide save/auto-update controls (for form context)
    onReplaceWithMaster?: () => void // Handler for replace with master option
    onCloneRunbook?: () => void // Handler for clone runbook option
    onSaveAsMaster?: () => void // Handler for save as master option
    readOnly?: boolean // Hide actions column and make fields read-only
}

export function RunbookTable({
    tasks,
    onSave,
    autoUpdate,
    onAutoUpdateChange,
    isSaving = false,
    hideToolbar = false,
    onReplaceWithMaster,
    onCloneRunbook,
    onSaveAsMaster,
    readOnly = false,
}: RunbookTableProps) {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
    const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null)
    const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null)
    const [dependenciesError, setDependenciesError] = useState<string | null>(null)
    const [localTasks, setLocalTasks] = useState<RunbookTask[]>(tasks || [])
    const [imagePickerIndex, setImagePickerIndex] = useState<number | null>(null)
    const [imagePickerOpen, setImagePickerOpen] = useState(false)

    // Refs to prevent useEffect from overwriting local edits
    const tasksInitializedRef = useRef(false)
    const isInitialMountRef = useRef(true)

    // Keep latest values in refs for stable callbacks
    const localTasksRef = useRef(localTasks)
    useEffect(() => {
        localTasksRef.current = localTasks
    }, [localTasks])

    const onSaveRef = useRef(onSave)
    useEffect(() => {
        onSaveRef.current = onSave
    }, [onSave])

    const autoUpdateRef = useRef(autoUpdate)
    useEffect(() => {
        autoUpdateRef.current = autoUpdate
    }, [autoUpdate])

    const viewModeRef = useRef(viewMode)
    useEffect(() => {
        viewModeRef.current = viewMode
    }, [viewMode])

    // Use the reusable calculateRunbookTimes utility function

    // Renumber tasks and update dependencies
    const renumberTasks = useCallback((taskList: RunbookTask[]): RunbookTask[] => {
        // Create mapping from old task numbers to new positions
        // First pass: map old task numbers to new positions
        const oldToNewMap = new Map<number, number>()
        taskList.forEach((task, newIndex) => {
            if (task.taskNo !== null) {
                oldToNewMap.set(task.taskNo, newIndex + 1)
            }
        })

        // Renumber tasks and update dependencies
        return taskList.map((task, index) => {
            const newTaskNo = index + 1

            if (index === 0) {
                // Task 1 has no dependencies
                return { ...task, taskNo: newTaskNo, dependencies: '' }
            }

            // Map old dependency task numbers to new ones
            const oldDeps = (task.dependencies || '').split(',').map(d => d.trim()).filter(d => d !== '')
            const newDeps = oldDeps
                .map(dep => {
                    const depNum = parseInt(dep, 10)
                    if (isNaN(depNum)) return ''
                    const newDepNum = oldToNewMap.get(depNum)
                    return newDepNum ? newDepNum.toString() : ''
                })
                .filter(d => d !== '')

            // Default dependency is the previous task if no valid dependencies
            // For tasks that had dependencies but they couldn't be mapped, use previous task
            const defaultDep = index.toString() // Previous task is at index - 1, which is taskNo = index
            const finalDeps = newDeps.length > 0 ? newDeps.join(', ') : defaultDep

            return {
                ...task,
                taskNo: newTaskNo,
                dependencies: finalDeps,
            }
        })
    }, [])

    const renumberTasksRef = useRef(renumberTasks)
    useEffect(() => {
        renumberTasksRef.current = renumberTasks
    }, [renumberTasks])

    const handleTaskChange = useCallback((index: number, field: keyof RunbookTask, value: any, saveImmediately = true) => {
        // Intercept image picker trigger
        if (field === 'imagePath' && value === '__PICK__') {
            setImagePickerIndex(index)
            setImagePickerOpen(true)
            return
        }


        const currentTasks = localTasksRef.current
        const updated = [...currentTasks]
        updated[index] = { ...updated[index], [field]: value }


        // If duration or dependencies changed, recalculate times
        if (field === 'duration' || field === 'dependencies') {
            const withTimes = calculateRunbookTimes(updated)

            // Mark that we're doing a local edit to prevent useEffect from overwriting
            isInitialMountRef.current = false
            tasksInitializedRef.current = true

            // Use functional update to ensure we're setting the latest calculated values
            setLocalTasks(() => {
                return withTimes
            })

            if (saveImmediately && autoUpdateRef.current) {
                onSaveRef.current(withTimes)
            }
        } else {

            // Mark that we're doing a local edit to prevent useEffect from overwriting
            isInitialMountRef.current = false
            tasksInitializedRef.current = true

            setLocalTasks(updated)

            if (saveImmediately && autoUpdateRef.current) {
                onSaveRef.current(updated)
            }
        }
    }, []) // No dependencies - uses refs

    const handleAddTask = useCallback(() => {
        const currentTasks = localTasksRef.current
        const lastTask = currentTasks[currentTasks.length - 1]
        const newTaskNo = currentTasks.length + 1
        const defaultDep = lastTask?.taskNo?.toString() || ''

        const newTask: RunbookTask = {
            taskNo: newTaskNo,
            task: '',
            commands: '',
            duration: 5, // Default 5m for new tasks
            startTime: null,
            finishTime: null,
            dependencies: defaultDep,
        }

        const updated = [...currentTasks, newTask]
        const withTimes = calculateRunbookTimes(updated)

        // Mark that we're doing a local edit to prevent useEffect from overwriting
        isInitialMountRef.current = false
        tasksInitializedRef.current = true

        setLocalTasks(withTimes)

        if (autoUpdateRef.current) {
            onSaveRef.current(withTimes)
        }

        // Open new task in edit mode when in card view
        if (viewMode === 'card') {
            setEditingTaskIndex(withTimes.length - 1)
        }
    }, [viewMode])

    const handleInsertTask = useCallback((index: number) => {
        const currentTasks = localTasksRef.current

        // Determine the dependency for the new task
        // It should depend on the task that will be before it after insertion
        // (which is the task currently at index - 1)
        const taskBefore = currentTasks[index - 1]
        const defaultDep = taskBefore?.taskNo?.toString() || ''

        const newTask: RunbookTask = {
            taskNo: null, // Will be renumbered
            task: '',
            commands: '',
            duration: 5, // Default 5m for new tasks
            startTime: null,
            finishTime: null,
            dependencies: defaultDep, // Set to the task number that will be before it
        }

        const updated = [...currentTasks]
        updated.splice(index, 0, newTask)
        const renumbered = renumberTasksRef.current(updated)

        // After renumbering, we need to:
        // 1. Set the inserted task's dependency (depends on the task before it)
        // 2. Update the task that was originally at the insertion point (now at index + 1)
        //    to depend on the newly inserted task
        const finalTasks = renumbered.map((task, idx) => {
            if (idx === index) {
                // This is the newly inserted task at position index (0-based)
                // It should depend on the previous task at position index - 1
                // After renumbering, that task has taskNo = index (1-based, since position index-1 = taskNo index)
                const prevTaskNo = index.toString()
                return { ...task, dependencies: prevTaskNo }
            } else if (idx === index + 1) {
                // This is the task that was originally at the insertion point (now shifted down)
                // It should depend on the newly inserted task (which is now at position index, taskNo = index + 1)
                const insertedTaskNo = (index + 1).toString()
                return { ...task, dependencies: insertedTaskNo }
            }
            return task
        })

        const withTimes = calculateRunbookTimes(finalTasks)

        // Mark that we're doing a local edit to prevent useEffect from overwriting
        isInitialMountRef.current = false
        tasksInitializedRef.current = true

        setLocalTasks(withTimes)

        if (autoUpdateRef.current) {
            onSaveRef.current(withTimes)
        }

        if (viewModeRef.current === 'card') {
            setEditingTaskIndex(index)
        }
    }, []) // No dependencies - uses refs

    const handleDeleteTask = useCallback((index: number) => {
        const currentTasks = localTasksRef.current
        const updated = currentTasks.filter((_, i) => i !== index)
        const renumbered = renumberTasksRef.current(updated)
        const withTimes = calculateRunbookTimes(renumbered)
        setLocalTasks(withTimes)

        if (autoUpdateRef.current) {
            onSaveRef.current(withTimes)
        }

        setDeleteConfirmIndex(null)
        const editing = editingTaskIndexRef.current
        if (editing === index) setEditingTaskIndex(null)
        else if (editing != null && editing > index) setEditingTaskIndex(editing - 1)
    }, [])

    // Add _index to tasks for row identification
    const tasksWithIndex = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return []
        return localTasks.map((task, index) => ({ ...task, _index: index }))
    }, [localTasks])

    const { i18n } = useTranslation()
    const columns = useMemo(() => {
        return createRunbookColumns(
            handleTaskChange,
            handleAddTask,
            handleInsertTask,
            handleDeleteTask,
            t,
            i18n.language,
            tasksWithIndex,
            readOnly
        )
    }, [handleTaskChange, handleAddTask, handleInsertTask, handleDeleteTask, t, i18n.language, tasksWithIndex, readOnly])

    const table = useReactTable({
        data: tasksWithIndex,
        columns,
        getCoreRowModel: getCoreRowModel(),
    })

    // Reset handler: discard local changes and reload from database
    const handleReset = useCallback(() => {
        if (tasks && tasks.length > 0) {
            setLocalTasks([...tasks]) // Create a new array to trigger re-render
            tasksInitializedRef.current = true
            isInitialMountRef.current = false
        }
    }, [tasks])

    // Sync local tasks with props ONLY on initial mount
    // After that, all work is done in localTasks state (in memory)
    // We ignore prop changes after initial load to prevent resetting calculated times
    useEffect(() => {
        // Only sync on initial mount when we first receive tasks from the server.
        // After that, NEVER sync from props — all work is done in localTasks, so the
        // parent's query updates don't reset our calculated times.
        if (isInitialMountRef.current && tasks && tasks.length > 0 && localTasks.length === 0) {
            setLocalTasks(tasks)
            tasksInitializedRef.current = true
            isInitialMountRef.current = false
        }
    }, [tasks])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
            setEditingTaskIndex(null)
            setDependenciesError(null)
        }
        }
        const handleClickOutside = (e: MouseEvent) => {
            if (editingTaskIndex !== null) {
                const target = e.target as HTMLElement
                if (!target.closest('[data-editing-card]')) {
                    setEditingTaskIndex(null)
                    setDependenciesError(null)
                }
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [editingTaskIndex])

    const handleDeleteConfirm = useCallback(() => {
        if (deleteConfirmIndex !== null) {
            handleDeleteTask(deleteConfirmIndex)
            setDeleteConfirmIndex(null)
        }
    }, [deleteConfirmIndex, handleDeleteTask])

    return (
        <div className="space-y-4">
            {/* View toggle: Card | Table */}
            <div className="flex flex-wrap items-center gap-4">
                <CardTableViewToggle
                    value={viewMode}
                    onValueChange={setViewMode}
                    cardLabel={t('objects.runbook.viewCard') || 'Card'}
                    tableLabel={t('objects.runbook.viewTable') || 'Table'}
                />
            </div>

            {/* Toolbar */}
            {!hideToolbar && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Switch
                                id="auto-update"
                                checked={autoUpdate}
                                onCheckedChange={onAutoUpdateChange}
                            />
                            <Label htmlFor="auto-update" className="text-sm">
                                {t('objects.runbook.autoUpdate')}
                            </Label>
                        </div>
                        {!autoUpdate && (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleReset}
                                >
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                    {t('objects.runbook.reset')}
                                </Button>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => onSaveRef.current(localTasksRef.current)}
                                    disabled={isSaving}
                                >
                                    <Save className="mr-2 h-4 w-4" />
                                    {isSaving ? t('objects.runbook.saving') : t('objects.runbook.save')}
                                </Button>
                            </>
                        )}
                        {!readOnly && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddTask}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        {t('objects.runbook.addTask')}
                    </Button>
                        )}
                    </div>
                    {(onReplaceWithMaster || onCloneRunbook || onSaveAsMaster) && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                >
                                    {t('objects.runbookOptions')}
                                    <ChevronDown className="ml-2 h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {onReplaceWithMaster && (
                                    <DropdownMenuItem onClick={onReplaceWithMaster}>
                                        {t('objects.replaceWithMaster')}
                                    </DropdownMenuItem>
                                )}
                                {onCloneRunbook && (
                                    <DropdownMenuItem onClick={onCloneRunbook}>
                                        {t('objects.cloneRunbook')}
                                    </DropdownMenuItem>
                                )}
                                {onSaveAsMaster && (
                                    <DropdownMenuItem onClick={onSaveAsMaster}>
                                        {t('objects.saveAsMaster')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            )}
            {hideToolbar && !readOnly && (
                <div className="flex justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddTask}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        {t('objects.runbook.addTask')}
                    </Button>
                </div>
            )}

            {viewMode === 'card' && (
                <div className="max-w-3xl">
                    {tasksWithIndex.length === 0 ? (
                        <div className="rounded-lg border p-8 text-center text-muted-foreground">
                            {t('objects.noRunbookSelected')}
                        </div>
                    ) : (
                        <div className="relative pl-6">
                        <div
                            className="absolute left-[43px] top-5 bottom-5 w-0.5 bg-gray-200"
                            aria-hidden
                        />
                        <div className="space-y-4">
                            {tasksWithIndex.map((task, idx) => (
                                <div
                                    key={task.id ?? idx}
                                    className="relative flex gap-4 items-start group"
                                >
                                    {/* Timeline node with insert above button on hover */}
                                    <div className="flex-shrink-0 flex flex-col items-center">
                                        {!readOnly && (
                                            <button
                                                type="button"
                                                onClick={() => handleInsertTask(idx)}
                                                className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-all opacity-0 group-hover:opacity-100 -mb-1 z-10"
                                                title={t('objects.runbook.insertAbove') || 'Insert task above'}
                                                aria-label={t('objects.runbook.insertAbove') || 'Insert task above'}
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-gray-500 bg-gray-300 text-gray-700 flex items-center justify-center text-sm font-semibold">
                                            {task.taskNo ?? '-'}
                                        </div>
                                    </div>
                                    <div
                                        data-editing-card={editingTaskIndex === idx ? '' : undefined}
                                        className={`flex-1 min-w-0 rounded-xl border-2 p-4 transition-all ${
                                            editingTaskIndex === idx
                                                ? 'border-blue-300 ring-2 ring-blue-200 bg-white'
                                                : 'border-gray-200 bg-white'
                                        }`}
                                    >
                                        {editingTaskIndex === idx && !readOnly ? (
                                            /* Edit mode: Task, Commands, Duration, Dependencies */
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.task')}</label>
                                                    <Textarea
                                                        value={task.task || ''}
                                                        onChange={(e) => handleTaskChange(idx, 'task', e.target.value, false)}
                                                        onBlur={(e) => handleTaskChange(idx, 'task', e.target.value)}
                                                        className="min-h-[80px] text-sm"
                                                        placeholder={t('objects.runbook.task')}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.commands')}</label>
                                                    <Textarea
                                                        value={task.commands || ''}
                                                        onChange={(e) => handleTaskChange(idx, 'commands', e.target.value, false)}
                                                        onBlur={(e) => handleTaskChange(idx, 'commands', e.target.value)}
                                                        className="min-h-[80px] text-sm font-mono resize-y"
                                                        placeholder={t('objects.runbook.commands') || 'Enter commands, one per line...'}
                                                    />
                                                </div>
                                                {/* Image */}
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.image', 'Image')}</label>
                                                    <div className="flex items-center gap-2">
                                                        {task.imagePath ? (
                                                            <div className="relative group">
                                                                <img
                                                                    src={`/images/${task.imagePath}`}
                                                                    alt=""
                                                                    className="h-16 w-24 rounded border object-cover cursor-pointer hover:opacity-80"
                                                                    onClick={() => handleTaskChange(idx, 'imagePath', '__PICK__')}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => handleTaskChange(idx, 'imagePath', null)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleTaskChange(idx, 'imagePath', '__PICK__')}
                                                            >
                                                                <ImageIcon className="h-4 w-4 mr-1.5 text-blue-600" />
                                                                {t('objects.runbook.addImage', 'Add Image')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-4">
                                                    <div>
                                                        <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.duration')}</label>
                                                        <Input
                                                            type="number"
                                                            value={task.duration ?? ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value === '' ? null : parseInt(e.target.value, 10)
                                                                if (!isNaN(v as number)) handleTaskChange(idx, 'duration', v, false)
                                                            }}
                                                            onBlur={(e) => {
                                                                const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                                                                if (!isNaN(v)) handleTaskChange(idx, 'duration', v)
                                                            }}
                                                            className="w-16 h-8 text-sm"
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-[180px]">
                                                        <label className="text-xs text-gray-500 block mb-1">{t('objects.runbook.dependencies')}</label>
                                                        {task.taskNo === 1 ? (
                                                            <Input value="" disabled className="h-8 text-sm" placeholder={t('objects.runbook.noDependencies')} />
                                                        ) : (
                                                            <>
                                                                <Input
                                                                    value={task.dependencies || ''}
                                                                    onChange={(e) => {
                                                                        handleTaskChange(idx, 'dependencies', e.target.value, false)
                                                                        const err = validateDependencies(e.target.value, tasksWithIndex, task.taskNo)
                                                                        setDependenciesError(err)
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        const v = e.target.value.trim()
                                                                        const err = validateDependencies(v, tasksWithIndex, task.taskNo)
                                                                        if (err) {
                                                                            setDependenciesError(err)
                                                                            return
                                                                        }
                                                                        setDependenciesError(null)
                                                                        handleTaskChange(idx, 'dependencies', v)
                                                                    }}
                                                                    placeholder="1, 2"
                                                                    className={`h-8 text-sm ${dependenciesError ? 'border-red-500' : ''}`}
                                                                />
                                                                {dependenciesError && (
                                                                    <p className="text-xs text-red-500 mt-1">{dependenciesError}</p>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Non-editing state */
                                            <>
                                                <div className="flex gap-3">
                                                    {/* Main content */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                <span className="flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                                    <Timer className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                                                    {task.duration != null ? `${task.duration}${t('objects.runbook.minutesShort') || 'm'}` : '-'}
                                                                </span>
                                                                {task.commands?.trim() ? (
                                                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                        <Code2 className="h-3.5 w-3.5" />
                                                                        {t('objects.runbook.hasCommands') || 'Has commands'}
                                                                    </span>
                                                                ) : null}
                                                                <span className="text-xs text-muted-foreground">
                                                                    {t('objects.runbook.dependencies')}: {task.taskNo === 1 ? t('objects.runbook.noDependencies') : (task.dependencies || '-')}
                                                                </span>
                                                            </div>
                                                            {!readOnly && (
                                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                                    {/* Add image button */}
                                                                    {!task.imagePath && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                handleTaskChange(idx, 'imagePath', '__PICK__')
                                                                            }}
                                                                            className="h-7 w-7 rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex items-center justify-center transition-colors"
                                                                            title={t('objects.runbook.addImage', 'Add image')}
                                                                        >
                                                                            <ImageIcon className="h-4 w-4" />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            setDependenciesError(null)
                                                                            setEditingTaskIndex(editingTaskIndex === idx ? null : idx)
                                                                }}
                                                                className="p-1 rounded text-blue-600 hover:text-blue-700 transition-colors"
                                                                title={t('common.edit') || 'Edit'}
                                                                aria-label={t('common.edit') || 'Edit'}
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmIndex(idx) }}
                                                                className="p-1 rounded text-red-500 hover:text-red-600 transition-colors"
                                                                title={t('common.delete') || 'Delete'}
                                                                aria-label={t('common.delete') || 'Delete'}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                        <p className="text-sm text-foreground">{task.task ? <TranslatableText text={task.task} /> : t('objects.runbook.task')}</p>
                                                        {task.commands?.trim() ? (
                                                            <details className="mt-3">
                                                                <summary className="text-xs text-gray-500 font-medium uppercase tracking-wide cursor-pointer">
                                                                    {t('objects.runbook.commands')}
                                                                </summary>
                                                                <pre className="mt-2 rounded-lg bg-gray-900 text-gray-100 p-3 font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                                                                    {task.commands}
                                                                </pre>
                                                            </details>
                                                        ) : null}
                                                    </div>
                                                    {/* Image on the right */}
                                                    {task.imagePath && (
                                                        <div className="flex-shrink-0 relative group">
                                                            <img
                                                                src={`/images/${task.imagePath}`}
                                                                alt=""
                                                                className="rounded-lg border w-32 h-24 object-cover cursor-pointer hover:opacity-90"
                                                                onClick={() => window.open(`/images/${task.imagePath}`, '_blank')}
                                                            />
                                                            {!readOnly && (
                                                                <button
                                                                    type="button"
                                                                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={(e) => { e.stopPropagation(); handleTaskChange(idx, 'imagePath', null) }}
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Add new task button at bottom */}
                        {!readOnly && (
                            <div className="mt-6 flex justify-center">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddTask}
                                    className="border-dashed"
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    {t('objects.runbook.addNewTask') || 'Add new task'}
                                </Button>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            )}
            {viewMode === 'table' && (
            <>
            <div className="rounded-md border overflow-visible runbook-table-wrapper">
                <Table className="w-full min-w-full table-auto">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="bg-table-header border-b border-table-border">
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="font-semibold text-foreground"
                                        style={{
                                            paddingTop: 'var(--table-header-padding-y)',
                                            paddingBottom: 'var(--table-header-padding-y)',
                                            paddingLeft: 'var(--table-header-padding-x)',
                                            paddingRight: 'var(--table-header-padding-x)',
                                            width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined,
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} className="hover:bg-muted/50">
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            style={{
                                                paddingTop: 'var(--table-cell-padding-y)',
                                                paddingBottom: 'var(--table-cell-padding-y)',
                                                paddingLeft: 'var(--table-cell-padding-x)',
                                                paddingRight: 'var(--table-cell-padding-x)',
                                                width: cell.column.getSize() !== 150 ? `${cell.column.getSize()}px` : undefined,
                                                overflow: 'visible',
                                                verticalAlign: 'top',
                                            }}
                                            className="overflow-visible"
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    {t('common.noResults')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <style>{`
                .runbook-table-wrapper .overflow-auto {
                    overflow: visible !important;
                }
                .runbook-table-wrapper table {
                    table-layout: auto !important;
                }
            `}</style>
            </>
            )}

            {/* Delete confirmation dialog */}
            <Dialog open={deleteConfirmIndex !== null} onOpenChange={(open) => !open && setDeleteConfirmIndex(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('objects.runbook.deleteTask') || 'Delete Task'}</DialogTitle>
                        <DialogDescription>
                            {t('objects.runbook.deleteTaskConfirm') || 'Are you sure you want to delete this task? This action cannot be undone.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmIndex(null)}>
                            {t('common.cancel') || 'Cancel'}
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteConfirm}>
                            {t('common.delete') || 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task Image Picker */}
            <TaskImagePicker
                open={imagePickerOpen}
                onOpenChange={(open) => {
                    setImagePickerOpen(open)
                    if (!open) setImagePickerIndex(null)
                }}
                onSelect={(imagePath) => {
                    if (imagePickerIndex !== null) {
                        handleTaskChange(imagePickerIndex, 'imagePath', imagePath)
                    }
                }}
                currentImagePath={imagePickerIndex !== null ? localTasks[imagePickerIndex]?.imagePath : null}
            />
        </div>
    )
}

